import { useEffect, useState } from "react";
import { _get } from "../services/apiClient";

// A product is "customizable" when the customer has to pick between more
// than one variant (Quarter / Half / Full ...).
export const isMultiVariant = (product) => (product?.variants || []).length > 1;

// What the customer pays per unit for a variant: offer price, else price.
export const variantPrice = (variant) =>
  Number(variant?.offerPrice || variant?.price || 0);

// A variant's own packageCharge (even 0) overrides the product's.
export const resolvePackageCharge = (product, variant) => {
  const own = variant?.packageCharge;
  return Number(own != null && own !== "" ? own : product?.packageCharge || 0);
};

// Cheapest variant, shown as the "from" price on the menu card.
export const cheapestVariant = (product) =>
  (product?.variants || []).reduce(
    (best, v) => (!best || variantPrice(v) < variantPrice(best) ? v : best),
    null,
  );

// Cart line id for a chosen variant (+ add-ons), so Half and Full - or the
// same variant with different add-ons - sit in the cart as separate lines.
export const variantLineId = (productId, variant, addOns = []) => {
  const addOnKey = addOns
    .map((a) => `${a.addOnId}x${a.quantity}`)
    .sort()
    .join(",");
  return `${productId}__${variant._id}${addOnKey ? `__${addOnKey}` : ""}`;
};

// Variant Unit master translations, fetched once per page load:
// { "Half": { ar: "نصف" }, ... } keyed by the unit's English display name.
let unitTranslationsPromise = null;
const loadUnitTranslations = () => {
  if (!unitTranslationsPromise) {
    unitTranslationsPromise = _get("/products/variant-units")
      .then((res) => {
        const map = {};
        (Array.isArray(res.data) ? res.data : []).forEach((u) => {
          map[u.displayName] = u.translations || {};
        });
        return map;
      })
      .catch(() => {
        unitTranslationsPromise = null;
        return {};
      });
  }
  return unitTranslationsPromise;
};

// Returns unit => label in the given language (English unit as fallback).
export const useVariantUnitLabel = (lang) => {
  const [translations, setTranslations] = useState({});
  useEffect(() => {
    let alive = true;
    loadUnitTranslations().then((map) => alive && setTranslations(map));
    return () => {
      alive = false;
    };
  }, []);
  return (unit) => (unit && translations[unit]?.[lang]) || unit || "";
};
